import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, DollarSign, Link2, ArrowRight, Mail, Lock, User, ChevronDown, ChevronUp, TrendingUp, Clock, Shield, BarChart3, Zap, Target, Globe, Instagram, Youtube, MessageCircle, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FAQ_DATA = [
  { q: "What commission rate do affiliates earn?", a: "Affiliates earn 15%+ on every qualified order placed through their unique referral link. With average order values around $250+, that's $37.50+ per sale." },
  { q: "When do affiliates get paid?", a: "Payouts are processed weekly. Once your balance reaches $25, you can request a payout via PayPal, Venmo, Cash App, or bank transfer." },
  { q: "How are affiliate earnings tracked?", a: "Every affiliate receives a unique referral link. When a customer clicks your link and makes a purchase within 180 days, you receive credit for that sale. Track everything in real-time on your personalized dashboard." },
  { q: "What discount do referrals receive?", a: "Customers who shop through your referral link receive exclusive savings on qualified orders, which helps boost your conversion rates and incentivizes first-time buyers." },
  { q: "What discount do affiliates receive?", a: "As an active affiliate partner, you receive a personal discount on your own orders from RENEW LABS." },
  { q: "Can I promote on any platform?", a: "Yes! You can share your referral link on any platform - Instagram, YouTube, TikTok, Twitter/X, blogs, email newsletters, podcasts, or your own website. We require a minimum of 1 post per week on your socials tagging @renewlabs." },
  { q: "Is there a minimum sales requirement?", a: "There is no minimum sales requirement to remain an active affiliate. However, we expect all partners to maintain at least 1 social media post or story per week mentioning RENEW LABS." },
  { q: "Can a business apply to be an affiliate?", a: "Absolutely! We welcome businesses, influencers, content creators, clinics, gyms, and wellness professionals. Our affiliate program is designed for anyone with an audience interested in research peptides." },
  { q: "Who to contact for affiliate support?", a: "Reach out to our affiliate team at support@getrenewlabs.com or call (817) 803-8745 for any questions, marketing materials, or partnership inquiries." },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#1E2532]" data-testid="faq-item">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group">
        <span className="text-white font-medium text-sm sm:text-base pr-4">{q}</span>
        {open ? <ChevronUp className="w-5 h-5 text-[#00F0FF] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#A1A7B5] group-hover:text-[#00F0FF] flex-shrink-0 transition-colors" />}
      </button>
      {open && <p className="text-[#A1A7B5] text-sm pb-5 leading-relaxed">{a}</p>}
    </div>
  );
};

const AffiliateSignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next');
  const [mode, setMode] = useState('signup');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', password: '', payment_method: 'paypal', payment_details: '',
    platforms: [], posting_frequency: '1-2 per week', audience_size: '', promotion_plan: '',
    social_handles: {}
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [agreement, setAgreement] = useState({ text: 'Loading agreement…', version: '', effective_date: '' });
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  useEffect(() => {
    axios.get(`${API}/affiliate/agreement`)
      .then(res => setAgreement(res.data))
      .catch(() => setAgreement(a => ({ ...a, text: 'Unable to load agreement. Please refresh the page.' })));
  }, []);
  const [showSignupForm, setShowSignupForm] = useState(false);

  const platformOptions = [
    { id: 'instagram', label: 'Instagram', icon: Instagram },
    { id: 'youtube', label: 'YouTube', icon: Youtube },
    { id: 'tiktok', label: 'TikTok', icon: Globe },
    { id: 'twitter', label: 'Twitter/X', icon: MessageCircle },
    { id: 'blog', label: 'Blog/Website', icon: Globe },
    { id: 'email', label: 'Email List', icon: Mail },
  ];

  const togglePlatform = (id) => {
    setForm(f => ({
      ...f,
      platforms: f.platforms.includes(id) ? f.platforms.filter(p => p !== id) : [...f.platforms, id]
    }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (form.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }
    if (!agreedToTerms) {
      toast.error('Please read and agree to the Renew Labs Affiliate Agreement');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/affiliate/register`, {
        name: form.name,
        email: form.email,
        password: form.password,
        payment_method: form.payment_method,
        payment_details: form.payment_details,
        agreed_to_terms: true,
        platforms: form.platforms,
        posting_frequency: form.posting_frequency,
        audience_size: form.audience_size,
        promotion_plan: form.promotion_plan,
        social_handles: form.social_handles,
      }, { withCredentials: true });
      toast.success(data.message || 'Application received!', { duration: 6000 });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/affiliate/login`, loginForm, { withCredentials: true });
      toast.success('Welcome back!');
      // If the user arrived with ?next=/some/path, send them there after login
      navigate(nextPath && nextPath.startsWith('/affiliate') ? nextPath : '/affiliate/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Default to login mode if we arrived here because login is required to continue
  useEffect(() => {
    if (nextPath) setMode('login');
  }, [nextPath]);

  return (
    <div className="min-h-screen bg-[#050505] py-12" data-testid="affiliate-signup-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 mb-6 text-xs font-semibold uppercase tracking-[0.2em] text-[#00F0FF] border border-[#00F0FF]/30 rounded-full bg-[#00F0FF]/5">
            Affiliate Program
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Become a Founding Partner with <span className="text-[#00F0FF]">RENEW LABS</span> and start earning today.
          </h1>
          <p className="text-lg text-[#A1A7B5] max-w-2xl mx-auto mb-8">
            We're Building Our Affiliate Network Intentionally.
          </p>
          <Button
            onClick={() => setShowSignupForm(true)}
            className="btn-primary text-lg px-10 py-4"
            data-testid="start-earning-btn"
          >
            Start Earning <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <div className="flex items-center justify-center gap-6 mt-6 text-sm text-[#A1A7B5]">
            <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-green-400" /> Free to join</span>
            <span className="flex items-center gap-1.5"><DollarSign className="w-4 h-4 text-green-400" /> $25 min payout</span>
            <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-green-400" /> Instant approval</span>
          </div>
        </div>

        {/* Commission Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <div className="glass-card p-8 text-center border border-[#00F0FF]/20">
            <p className="text-5xl font-extrabold text-[#00F0FF] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>15%+</p>
            <p className="text-white font-bold mb-4">Lifetime Commission</p>
            <p className="text-[#A1A7B5] text-sm mb-6">Earn 15%+ on every qualified order</p>
            <div className="space-y-2 text-left">
              <div className="flex items-center gap-2 text-sm"><Shield className="w-4 h-4 text-[#00F0FF]" /><span className="text-[#A1A7B5]">Average order value: $250+</span></div>
              <div className="flex items-center gap-2 text-sm"><Shield className="w-4 h-4 text-[#00F0FF]" /><span className="text-[#A1A7B5]">Avg commission per order: <span className="text-white font-semibold">$37.50+</span> <span className="text-xs text-[#6B7280]">(15% × $250)</span></span></div>
              <div className="flex items-center gap-2 text-sm items-start"><Shield className="w-4 h-4 text-[#00F0FF] flex-shrink-0 mt-0.5" /><span className="text-[#A1A7B5]"><span className="text-[#00F0FF] font-semibold">Example:</span> 5 referrals/day × $37.50 × 30 days = <span className="text-white font-semibold">$5,625/month</span></span></div>
            </div>
          </div>
          <div className="glass-card p-8 text-center border border-purple-500/20">
            <p className="text-5xl font-extrabold text-purple-400 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>15%</p>
            <p className="text-white font-bold mb-4">Customer Discount</p>
            <p className="text-[#A1A7B5] text-sm mb-6">Give your audience exclusive savings on qualified orders</p>
            <div className="space-y-2 text-left">
              <div className="flex items-center gap-2 text-sm"><Shield className="w-4 h-4 text-purple-400" /><span className="text-[#A1A7B5]">Boosts conversion rates</span></div>
              <div className="flex items-center gap-2 text-sm"><Shield className="w-4 h-4 text-purple-400" /><span className="text-[#A1A7B5]">Incentivizes first-time buyers</span></div>
              <div className="flex items-center gap-2 text-sm"><Shield className="w-4 h-4 text-purple-400" /><span className="text-[#A1A7B5]">Optional vanity code tied to your account</span></div>
            </div>
          </div>
        </div>

        {/* Earning Tiers */}
        <div className="glass-card p-8 mb-16" data-testid="earning-tiers">
          <h2 className="text-2xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Example Earnings</h2>
          <p className="text-[#A1A7B5] text-center text-sm mb-2">Illustrative scenarios based on our 15% commission and $250 average order value</p>
          <p className="text-[#6B7280] text-center text-xs mb-8">Math: <span className="text-[#00F0FF]">referrals × $37.50</span> (15% of $250 avg. order) <span className="hidden sm:inline">×</span> <span className="sm:hidden">over</span> 30 days</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-[#111621] rounded-xl">
              <p className="text-[#00F0FF] text-sm font-semibold uppercase tracking-wider mb-2">Starter Partner</p>
              <p className="text-[#A1A7B5] text-xs mb-4">A focused creator with steady content</p>
              <p className="text-white font-bold">3 referrals per week</p>
              <p className="text-[#A1A7B5] text-sm">= 12 referrals per month</p>
              <p className="text-2xl font-extrabold text-white mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>~$450<span className="text-sm text-[#A1A7B5] font-normal">/month</span></p>
              <p className="text-[#6B7280] text-[11px] mt-2">Example: 12 × $37.50 = $450</p>
            </div>
            <div className="text-center p-6 bg-[#111621] rounded-xl border border-[#00F0FF]/30">
              <p className="text-[#00F0FF] text-sm font-semibold uppercase tracking-wider mb-2">Growth Partner</p>
              <p className="text-[#A1A7B5] text-xs mb-4">Consistent traffic with engaged audience</p>
              <p className="text-white font-bold">2 referrals per day</p>
              <p className="text-[#A1A7B5] text-sm">= 60 referrals per month</p>
              <p className="text-2xl font-extrabold text-white mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>~$2,250<span className="text-sm text-[#A1A7B5] font-normal">/month</span></p>
              <p className="text-[#6B7280] text-[11px] mt-2">Example: 60 × $37.50 = $2,250</p>
            </div>
            <div className="text-center p-6 bg-[#111621] rounded-xl">
              <p className="text-[#00F0FF] text-sm font-semibold uppercase tracking-wider mb-2">Elite Partner</p>
              <p className="text-[#A1A7B5] text-xs mb-4">High-volume with strong conversion</p>
              <p className="text-white font-bold">9 referrals per day</p>
              <p className="text-[#A1A7B5] text-sm">= ~267 referrals per month</p>
              <p className="text-2xl font-extrabold text-white mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>~$10,000<span className="text-sm text-[#A1A7B5] font-normal">/month</span></p>
              <p className="text-[#6B7280] text-[11px] mt-2">Example: 267 × $37.50 ≈ $10,013</p>
            </div>
          </div>
          <p className="text-[#6B7280] text-[11px] text-center mt-6 italic max-w-2xl mx-auto">
            Figures shown are illustrative examples calculated from the 15% commission rate and $250 average order value. Actual earnings depend on your audience, engagement, and promotion consistency — RENEW LABS does not guarantee any specific income.
          </p>
        </div>

        {/* The Details */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>The Details</h2>
          <p className="text-[#A1A7B5] text-center text-sm mb-8">Why Partner With RENEW LABS</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: DollarSign, title: "High Order Values", desc: "With average carts around $250+, each referral carries meaningful commission potential - not pennies per click.", color: "text-green-400" },
              { icon: Clock, title: "Flexible Payouts", desc: "Receive weekly payments based on your performance, with multiple pay options available.", color: "text-[#00F0FF]" },
              { icon: Zap, title: "Fast Fulfillment", desc: "Orders processed within 24-48 hours with standard delivery up to 7 days. Express options available.", color: "text-yellow-400" },
              { icon: Target, title: "180-Day Tracking Window", desc: "You receive credit for qualified purchases made within 180 days of referral. Long cookie window means more conversions.", color: "text-purple-400" },
              { icon: BarChart3, title: "Personalized Dashboard", desc: "Track clicks, conversions, and commission earnings instantly. Full transparency on your performance.", color: "text-blue-400" },
              { icon: TrendingUp, title: "High Conversion Rates", desc: "Premium research compounds with strong retention and repeat purchasing. Products that actually convert.", color: "text-red-400" },
            ].map((item, i) => (
              <div key={i} className="glass-card p-6">
                <item.icon className={`w-6 h-6 ${item.color} mb-3`} />
                <h3 className="text-white font-bold text-sm mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                <p className="text-[#A1A7B5] text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex items-center justify-center gap-8 mb-16 text-sm text-[#A1A7B5]">
          <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#00F0FF]" /> Weekly payouts</span>
          <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#00F0FF]" /> $25 minimum</span>
          <span className="flex items-center gap-2"><Target className="w-4 h-4 text-[#00F0FF]" /> 180-day tracking</span>
        </div>

        {/* FAQ */}
        <div className="glass-card p-6 sm:p-8 mb-16" data-testid="affiliate-faq">
          <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Frequently Asked Questions</h2>
          {FAQ_DATA.map((faq, i) => <FaqItem key={i} q={faq.q} a={faq.a} />)}
        </div>

        {/* Signup / Login Form */}
        <div className="max-w-lg mx-auto" id="signup-form">
          <div className="glass-card p-8" data-testid="affiliate-form-card">
            <div className="flex mb-6 bg-[#111621] rounded-lg p-1">
              <button
                onClick={() => setMode('signup')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${mode === 'signup' ? 'bg-[#00F0FF] text-black' : 'text-[#A1A7B5]'}`}
                data-testid="affiliate-tab-signup"
              >
                Apply Now
              </button>
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${mode === 'login' ? 'bg-[#00F0FF] text-black' : 'text-[#A1A7B5]'}`}
                data-testid="affiliate-tab-login"
              >
                Log In
              </button>
            </div>

            {mode === 'signup' ? (
              submitted ? (
                <div className="glass-card p-8 text-center" data-testid="application-received">
                  <div className="w-16 h-16 bg-[#00F0FF]/10 border-2 border-[#00F0FF] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-[#00F0FF]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Application Received</h3>
                  <p className="text-[#A1A7B5] text-sm leading-relaxed mb-4">
                    Thanks for applying! We've sent a confirmation to <span className="text-[#00F0FF]">{form.email}</span>. Our team reviews each application personally and typically responds within <span className="text-white font-semibold">1&ndash;2 business days</span>.
                  </p>
                  <p className="text-[#A1A7B5] text-sm leading-relaxed mb-6">
                    Once approved, you'll get a welcome email with your referral code, dashboard access, and a link to set up your Stripe payout and 1099 tax info.
                  </p>
                  <Button onClick={() => { setSubmitted(false); setMode('login'); }} className="btn-secondary" data-testid="back-to-login-btn">
                    Return to Login
                  </Button>
                </div>
              ) : (
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="relative">
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name" required className="input-field w-full pl-10" data-testid="affiliate-name" />
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
                </div>
                <div className="relative">
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" required className="input-field w-full pl-10" data-testid="affiliate-email" />
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
                </div>
                <div className="relative">
                  <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" required minLength={6} className="input-field w-full pl-10" data-testid="affiliate-password" />
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
                </div>

                {/* Platforms */}
                <div>
                  <label className="text-[#A1A7B5] text-sm mb-2 block">Where do you plan to promote? *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {platformOptions.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => togglePlatform(p.id)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
                          form.platforms.includes(p.id)
                            ? 'bg-[#00F0FF]/15 border border-[#00F0FF] text-[#00F0FF]'
                            : 'bg-[#111621] border border-[#1E2532] text-[#A1A7B5] hover:border-[#00F0FF]/50'
                        }`}
                        data-testid={`platform-${p.id}`}
                      >
                        <p.icon className="w-3.5 h-3.5" />
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Social handles (shown for each selected platform that has a handle concept) */}
                {form.platforms.filter(id => ['instagram', 'youtube', 'tiktok', 'twitter'].includes(id)).length > 0 && (
                  <div className="bg-[#0A0C10] border border-[#1E2532] rounded-lg p-4 space-y-2.5" data-testid="social-handles-block">
                    <label className="text-white text-sm font-medium">Your handles / channel URLs <span className="text-[#A1A7B5] font-normal">(so we can verify your audience)</span></label>
                    {form.platforms.includes('instagram') && (
                      <div className="flex items-center gap-2">
                        <Instagram className="w-4 h-4 text-[#00F0FF] flex-shrink-0" />
                        <input type="text" placeholder="@yourinstagram" value={form.social_handles.instagram || ''} onChange={(e) => setForm(f => ({ ...f, social_handles: { ...f.social_handles, instagram: e.target.value } }))} className="input-field w-full text-sm py-2" data-testid="handle-instagram" />
                      </div>
                    )}
                    {form.platforms.includes('youtube') && (
                      <div className="flex items-center gap-2">
                        <Youtube className="w-4 h-4 text-[#00F0FF] flex-shrink-0" />
                        <input type="text" placeholder="YouTube channel URL or @handle" value={form.social_handles.youtube || ''} onChange={(e) => setForm(f => ({ ...f, social_handles: { ...f.social_handles, youtube: e.target.value } }))} className="input-field w-full text-sm py-2" data-testid="handle-youtube" />
                      </div>
                    )}
                    {form.platforms.includes('tiktok') && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-[#00F0FF] flex-shrink-0" />
                        <input type="text" placeholder="@yourtiktok" value={form.social_handles.tiktok || ''} onChange={(e) => setForm(f => ({ ...f, social_handles: { ...f.social_handles, tiktok: e.target.value } }))} className="input-field w-full text-sm py-2" data-testid="handle-tiktok" />
                      </div>
                    )}
                    {form.platforms.includes('twitter') && (
                      <div className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-[#00F0FF] flex-shrink-0" />
                        <input type="text" placeholder="@yourhandle" value={form.social_handles.twitter || ''} onChange={(e) => setForm(f => ({ ...f, social_handles: { ...f.social_handles, twitter: e.target.value } }))} className="input-field w-full text-sm py-2" data-testid="handle-twitter" />
                      </div>
                    )}
                  </div>
                )}

                {/* Audience Size */}
                <div>
                  <label className="text-[#A1A7B5] text-sm mb-1 block">Approximate audience size</label>
                  <select value={form.audience_size} onChange={(e) => setForm({ ...form, audience_size: e.target.value })} className="input-field w-full" data-testid="affiliate-audience-size">
                    <option value="">Select...</option>
                    <option value="0-1000">0 - 1,000</option>
                    <option value="1000-10000">1,000 - 10,000</option>
                    <option value="10000-50000">10,000 - 50,000</option>
                    <option value="50000-100000">50,000 - 100,000</option>
                    <option value="100000+">100,000+</option>
                  </select>
                </div>

                {/* Posting Commitment */}
                <div className="bg-[#111621] border border-[#1E2532] rounded-lg p-4">
                  <p className="text-white text-sm font-medium mb-2">Posting Requirement</p>
                  <p className="text-[#A1A7B5] text-xs leading-relaxed">
                    All affiliates are required to post a minimum of <span className="text-[#00F0FF] font-semibold">1 post or story per week</span> on their social media tagging <span className="text-white font-semibold">@renewlabs</span>. This helps maintain active partnerships and program integrity.
                  </p>
                </div>

                {/* Payment */}
                <div>
                  <label className="text-[#A1A7B5] text-sm mb-1 block">Preferred Payout Method</label>
                  <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="input-field w-full" data-testid="affiliate-payment-method">
                    <option value="paypal">PayPal</option>
                    <option value="venmo">Venmo</option>
                    <option value="cashapp">Cash App</option>
                    <option value="bank">Bank Transfer</option>
                  </select>
                </div>
                <div className="relative">
                  <input type="text" value={form.payment_details} onChange={(e) => setForm({ ...form, payment_details: e.target.value })} placeholder="PayPal email or payment handle" className="input-field w-full" data-testid="affiliate-payment-details" />
                </div>

                {/* Affiliate Agreement */}
                <div data-testid="affiliate-agreement-block">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#00F0FF]" />
                      <label className="text-white text-sm font-medium">Affiliate Agreement</label>
                    </div>
                    {agreement.version && (
                      <span className="text-[#A1A7B5] text-xs font-mono" data-testid="agreement-version">
                        {agreement.version} · {agreement.effective_date}
                      </span>
                    )}
                  </div>
                  <div
                    className="bg-[#0A0C10] border border-[#1E2532] rounded-lg p-4 h-48 overflow-y-auto text-[#A1A7B5] text-xs leading-relaxed whitespace-pre-wrap font-mono"
                    data-testid="affiliate-agreement-text"
                  >
                    {agreement.text}
                  </div>
                  <label className="flex items-start gap-2.5 mt-3 cursor-pointer select-none group">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-[#1E2532] bg-[#111621] text-[#00F0FF] focus:ring-[#00F0FF] focus:ring-offset-0 cursor-pointer"
                      data-testid="affiliate-agreement-checkbox"
                    />
                    <span className="text-[#A1A7B5] text-xs leading-relaxed group-hover:text-white transition-colors">
                      I have read and agree to the <span className="text-[#00F0FF] font-semibold">Renew Labs Affiliate Agreement</span>
                    </span>
                  </label>
                </div>

                <Button type="submit" disabled={loading || !agreedToTerms} className="btn-primary w-full py-3 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="affiliate-signup-btn">
                  <span>{loading ? 'Creating Account...' : 'Become a Partner'}</span>
                  {!loading && <ArrowRight className="w-5 h-5" />}
                </Button>
              </form>
              )
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="relative">
                  <input type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="Email" required className="input-field w-full pl-10" data-testid="affiliate-login-email" />
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
                </div>
                <div className="relative">
                  <input type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Password" required className="input-field w-full pl-10" data-testid="affiliate-login-password" />
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
                </div>
                <Button type="submit" disabled={loading} className="btn-primary w-full py-3" data-testid="affiliate-login-btn">
                  {loading ? 'Logging in...' : 'Log In'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateSignupPage;
